/**
 * 生成图片的下载:
 *  - base64 结果 (data: URL) 与本地 blob: URL 同源, 直接 <a download> 本地保存;
 *  - ARK 返回的 https 图片在 TOS 上, 跨域 —— <a download> 会被浏览器忽略而开新标签页。
 *    改走后端 GET /api/image/download (同源, Content-Disposition: attachment)。
 *    <a href> 带不了 Bearer token, 所以用 fetch(带 token) → blob → objectURL → 点击保存。
 */
import { apiFetchBlob } from "@/lib/api-client";

const EXT_BY_MIME: Record<string, string> = {
  "image/png": ".png",
  "image/jpeg": ".jpg",
  "image/jpg": ".jpg",
  "image/webp": ".webp",
  "image/gif": ".gif",
};

/** "ai_image_1" + "image/png" → "ai_image_1.png"; 未知类型用 .jpg (与旧版一致) */
export function imageFilename(base: string, mime: string | null | undefined): string {
  const ext = EXT_BY_MIME[(mime ?? "").split(";")[0].trim().toLowerCase()] ?? ".jpg";
  return `${base}${ext}`;
}

/** data:image/png;base64,... → "image/png" */
export function dataUrlMime(src: string): string | null {
  const m = /^data:([^;,]+)[;,]/i.exec(src);
  return m ? m[1] : null;
}

export function isLocalImageSrc(src: string): boolean {
  return src.startsWith("data:") || src.startsWith("blob:");
}

export function imageDownloadPath(url: string, filename: string): string {
  return `/api/image/download?url=${encodeURIComponent(url)}&filename=${encodeURIComponent(filename)}`;
}

function saveHref(href: string, filename: string) {
  const a = document.createElement("a");
  a.href = href;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/** 下载一张生成图片; 失败抛异常 (ApiError 等), 由调用方提示 */
export async function downloadImage(src: string, base: string): Promise<void> {
  if (isLocalImageSrc(src)) {
    saveHref(src, imageFilename(base, dataUrlMime(src)));
    return;
  }
  const blob = await apiFetchBlob(imageDownloadPath(src, base), { timeoutMs: 60_000 });
  const objectUrl = URL.createObjectURL(blob);
  try {
    saveHref(objectUrl, imageFilename(base, blob.type));
  } finally {
    // 给浏览器一点时间开始保存再回收
    setTimeout(() => URL.revokeObjectURL(objectUrl), 10_000);
  }
}
